import { i18n } from "./i18n";

const LOCALE_MAP = { es: "es-AR", it: "it-IT", en: "en-US", pt: "pt-BR" };

export const getLocale = () => LOCALE_MAP[i18n.language] || "es-AR";

export const fmtTime = (d, options = { hour: "2-digit", minute: "2-digit" }) =>
  new Date(d).toLocaleTimeString(getLocale(), options);

export const fmtDate = (d, options = { day: "2-digit", month: "2-digit", year: "numeric" }) =>
  new Date(d).toLocaleDateString(getLocale(), options);

export const fmtDateHeader = (d) =>
  new Date(d).toLocaleDateString(getLocale(), { weekday: "long", day: "numeric", month: "long" });

export const fmtDateLong = (d) =>
  new Date(d).toLocaleDateString(getLocale(), {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

export const fmtDateTimeShort = (d, options = { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) =>
  new Date(d).toLocaleString(getLocale(), options);

/**
 * feat_315 — Nombre del día en minúscula, según locale.
 * Ej: es-AR "lunes", it-IT "lunedì", pt-BR "segunda-feira", en-US "Monday".
 */
export const fmtDayName = (d, options = {}) => {
  const { timezone } = options;
  const opts = { weekday: "long", ...(timezone && { timeZone: timezone }) };
  try {
    return new Date(d).toLocaleDateString(getLocale(), opts);
  } catch {
    return "";
  }
};

/**
 * feat_315 — Devuelve DD/MM respetando el orden del locale (en-US → MM/DD).
 * Usa formatToParts para forzar 2-digit y descartar la parte del año.
 * ICU ignora el 2-digit cuando le pedís solo día+mes; con year + descarte, respeta el padding.
 */
const fmtDayMonth = (d, options = {}) => {
  const { timezone } = options;
  const opts = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(timezone && { timeZone: timezone })
  };
  try {
    const parts = new Intl.DateTimeFormat(getLocale(), opts).formatToParts(new Date(d));
    // Filtrar la parte del año y el literal ("/") inmediatamente anterior o posterior a ella
    const filtered = [];
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      if (p.type === "year") continue;
      if (p.type === "literal") {
        const prev = parts[i - 1];
        const next = parts[i + 1];
        if ((prev && prev.type === "year") || (next && next.type === "year")) continue;
      }
      filtered.push(p);
    }
    return filtered.map(p => p.value).join("");
  } catch {
    return "";
  }
};

/**
 * feat_315 — startOfDay en la timezone dada. Necesario para comparar días calendario
 * correctamente (un mensaje de "ayer 23:50" visto "hoy 00:10" debe ser "ayer",
 * no "hoy" por diferencia de 20 minutos).
 */
export const startOfDayInTz = (d, timezone) => {
  const date = new Date(d);
  if (!timezone) {
    // Sin timezone: día local del navegador
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  }
  // Con timezone: extraer y/m/d en esa tz vía formatToParts
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(date);
    const y = parseInt(parts.find(p => p.type === "year").value, 10);
    const m = parseInt(parts.find(p => p.type === "month").value, 10);
    const dy = parseInt(parts.find(p => p.type === "day").value, 10);
    return Date.UTC(y, m - 1, dy);
  } catch {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  }
};

/**
 * feat_315 — Valida la timezone antes de pasársela a Intl. Una tz inválida
 * (dato viejo/corrupto en Settings.businessTimezone) hace THROW en
 * toLocaleTimeString → tumbaría el render de todo el hilo desde dentro del .map.
 * Devuelve la tz si es usable, o undefined (→ tz del navegador).
 */
const safeTimezone = (timezone) => {
  if (!timezone) return undefined;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone }).format(new Date());
    return timezone;
  } catch {
    return undefined;
  }
};

/**
 * feat_315 — Formato inteligente tipo WhatsApp real.
 *
 * Umbrales (comparación por día calendario, no por horas transcurridas):
 *   hoy o futuro         → "12:35"              (solo hora)
 *   ayer                 → "Ayer 09:15"         (i18n smartTimestamp.yesterday + hora)
 *   2..6 días atrás      → "lunes 18:00"        (nombre del día + hora)
 *   más viejo, mismo año → "20/08 12:35"        (día/mes + hora, orden del locale)
 *   otro año             → "20/08/2025 12:35"   (fecha completa + hora)
 *
 * Reglas:
 * - Hora SIEMPRE con hour12:false (evita "06:00 p. m." en es-AR)
 * - DD/MM vía formatToParts (evita "17/8" en es-AR)
 * - Comparación por día calendario en la tz dada
 * - Fecha inválida/nula → "" (nunca throw, nunca "Invalid Date")
 *
 * @param {Date|string} date
 * @param {{timezone?: string}} [options]
 * @returns {string}
 */
export const fmtSmartTimestamp = (date, options = {}) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const timezone = safeTimezone(options.timezone);

  try {
    const now = new Date();
    const timeOpts = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      ...(timezone && { timeZone: timezone })
    };
    const time = d.toLocaleTimeString(getLocale(), timeOpts);

    const startD = startOfDayInTz(d, timezone);
    const startNow = startOfDayInTz(now, timezone);
    const daysDiff = Math.round((startNow - startD) / 86400000);

    // Hoy o futuro (mensaje programado o reloj del cliente adelantado)
    if (daysDiff <= 0) return time;

    // Ayer
    if (daysDiff === 1) {
      return `${i18n.t("smartTimestamp.yesterday")} ${time}`;
    }

    // 2..6 días atrás → nombre del día
    if (daysDiff <= 6) {
      return `${fmtDayName(d, { timezone })} ${time}`;
    }

    // Más viejo: comparar el año en la tz de referencia
    const yearD = timezone
      ? parseInt(new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric" }).format(d), 10)
      : d.getFullYear();
    const yearNow = timezone
      ? parseInt(new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric" }).format(now), 10)
      : now.getFullYear();

    if (yearD === yearNow) {
      return `${fmtDayMonth(d, { timezone })} ${time}`;
    }

    // Otro año → fecha completa
    const fullDateOpts = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      ...(timezone && { timeZone: timezone })
    };
    const fullDate = d.toLocaleDateString(getLocale(), fullDateOpts);
    return `${fullDate} ${time}`;
  } catch {
    // Última red: un timestamp NUNCA puede tumbar el render de un hilo.
    return "";
  }
};

/**
 * feat_341 — Separador de día tipo WhatsApp ("Hoy" / "Ayer" / "lunes" / "20/08").
 *
 * Es la píldora centrada que va ENTRE mensajes, no el timestamp del globo
 * (ese es fmtSmartTimestamp). Lógica nueva a propósito: el chat principal
 * (MessagesList.renderDailyTimestamps) imprime `dd/MM/yyyy` crudo y compara con
 * `isSameDay` de date-fns — que compara en la tz del NAVEGADOR. Acá se compara
 * con startOfDayInTz en la tz de la EMPRESA, coherente con fmtSmartTimestamp.
 *
 * Umbrales (día calendario en la tz dada):
 *   hoy o futuro         → "Hoy"          (i18n smartTimestamp.today)
 *   ayer                 → "Ayer"         (i18n smartTimestamp.yesterday)
 *   2..6 días atrás      → "lunes"        (nombre del día, localizado)
 *   más viejo, mismo año → "20/08"
 *   otro año             → "20/08/2025"
 *
 * Fecha inválida/nula o tz rota → "" (nunca throw: se renderiza dentro de un .map).
 *
 * @param {Date|string} date
 * @param {{timezone?: string}} [options]
 * @returns {string}
 */
export const fmtDaySeparator = (date, options = {}) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const timezone = safeTimezone(options.timezone);

  try {
    const now = new Date();
    const startD = startOfDayInTz(d, timezone);
    const startNow = startOfDayInTz(now, timezone);
    const daysDiff = Math.round((startNow - startD) / 86400000);

    if (daysDiff <= 0) return i18n.t("smartTimestamp.today");
    if (daysDiff === 1) return i18n.t("smartTimestamp.yesterday");
    if (daysDiff <= 6) return fmtDayName(d, { timezone });

    const yearD = timezone
      ? parseInt(new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric" }).format(d), 10)
      : d.getFullYear();
    const yearNow = timezone
      ? parseInt(new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric" }).format(now), 10)
      : now.getFullYear();

    if (yearD === yearNow) return fmtDayMonth(d, { timezone });

    return d.toLocaleDateString(getLocale(), {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      ...(timezone && { timeZone: timezone })
    });
  } catch {
    return "";
  }
};
